/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PreviewType, ScreenType } from 'client-code/previews';

import { DEFAULT_RETRY_OPTIONS } from '../../../../../config';
import { ServicesClientImpl } from '../../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../../core/locale';
import { Log } from '../../../../../core/logging';
import { NetworkManager } from '../../../../../core/network/NetworkManager';
import { RetryMechanism } from '../../../../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../../../../core/retrymechanism/RetryOptions';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../../core/tivo/tivo-redux-wrapper';
import { TOfferPreview } from '../../../../../core/types';
import { onSetToDoListSelectedRowPreviewAction } from '../../../../../store/settings/todolist/todolist.actions';
import AiringInfo from '../../Preview/AiringInfo';
import ConflictInfo from '../../Preview/ConflictInfo';
import DescriptionInfo from '../../Preview/DescriptionInfo';
import PreviewBottom from '../../Preview/PreviewBottom';
import TopInfo from '../../Preview/TopInfo';
import { getTodoItemsFromPage, mapToDoItemConflictToMessageKey } from '../ToDoList.utils';

import './TodoListPreview.scss';

const TAG = 'ToDoListPreview';
const DEBOUNCE_VALUE = 300;
const services = new ServicesClientImpl();

export const TodoListPreview = () => {
    const { verticalPosition, currentPage, toDoItems } = useTivoSelector(
        (state: RootState) => state.todoList,
    );
    const dispatch = useTivoDispatch();
    const shouldHideMetadata = services.shouldHideMetadata();
    const [offerPreviewInfo, setOfferPreviewInfo] = useState<{
        preview: TOfferPreview;
        offerId: string;
    } | null>(null);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const selectedTodoItem = useMemo(() => {
        const { actualItems } = getTodoItemsFromPage(toDoItems, currentPage);
        return actualItems[verticalPosition];
    }, [currentPage, toDoItems, verticalPosition]);

    const fetchPreview = useCallback(async (offerId: string) => {
        if (!offerId) return;
        try {
            const fetchOfferPreview = services.fetchOfferPreviewV2.bind(services);
            const preview = RetryMechanism.callServiceWithRetry<TOfferPreview>(
                fetchOfferPreview,
                [offerId, ScreenType.All],
                DEFAULT_RETRY_OPTIONS as RetryOptions,
            );
            dispatch(onSetToDoListSelectedRowPreviewAction(preview));
            preview.then((data) => {
                if (data) {
                    setOfferPreviewInfo({ preview: data, offerId });
                }
            });
        } catch (error) {
            Log.e(TAG, `Failed to fetch preview: ${error}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setOfferPreviewInfo(null);
        dispatch(onSetToDoListSelectedRowPreviewAction(null));
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            if (selectedTodoItem?.offerId) {
                fetchPreview(selectedTodoItem.offerId);
            }
        }, DEBOUNCE_VALUE);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTodoItem?.offerId, fetchPreview]);

    if (!offerPreviewInfo || !selectedTodoItem || NetworkManager.isNetworkDisconnected())
        return null;

    const { offerPreview } = offerPreviewInfo.preview;
    if (!offerPreview) return null;

    const { contentPreview } = offerPreview;

    if (!contentPreview) return null;

    if (offerPreviewInfo.offerId !== selectedTodoItem.offerId) {
        return null;
    }

    const {
        startTime: startTimeStr,
        endTime: endTimeStr,
        description,
        isAdult,
        movieYear,
        previewType,
        seasonNumber,
        episodeNumber,
        episodeTitle,
        title,
        channel,
    } = contentPreview;

    const startTime = startTimeStr ? Date.parse(startTimeStr) : 0;
    const endTime = endTimeStr ? Date.parse(endTimeStr) : 0;
    const videoResolution = selectedTodoItem?.videoResolution?.toLowerCase();
    const isMovie = previewType === PreviewType.MoviePreview;
    const hideMetadata = shouldHideMetadata ? !!isAdult : false;

    const { preview } = mapToDoItemConflictToMessageKey(selectedTodoItem);

    return (
        <div className="todo-list-preview">
            <AiringInfo
                startTime={startTime}
                endTime={endTime}
                channelNumber={channel?.channelNumber}
                callSign={channel?.callSign}
                videoResolution={videoResolution}
            />
            <ConflictInfo selectedTodoItem={selectedTodoItem} preview={preview} />
            <TopInfo
                title={title}
                episodeNumber={episodeNumber}
                episodeTitle={episodeTitle}
                episodeSeason={seasonNumber}
                isMovie={isMovie}
                movieYear={movieYear}
                hideMetadata={hideMetadata}
            />
            <DescriptionInfo
                description={
                    hideMetadata
                        ? Locale.getString(LocaleKeys.PREVIEW_HIDDEN_DESCRIPTION)
                        : description
                }
            />
            {!hideMetadata && <PreviewBottom contentPreview={contentPreview} />}
        </div>
    );
};
